import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Camera,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  ExternalLink,
  MessageCircle,
  Send,
  Upload,
} from "lucide-react";
import {
  api,
  ApiError,
  type CommentRow,
  type ProjectDetail,
  type Requirement,
} from "../../api";
import { hasAnyRole, useAuth } from "../../auth";
import { cx } from "../../ui";

/**
 * Project-level inspector view. Stripped to the moves a PM or
 * Chief Engineer would actually make while standing in the unit:
 *
 *   1. Take a photo (camera capture, single tap to attach).
 *   2. Drop a quick note (becomes a comment on the thread).
 *   3. Mark inspection signed off OR work complete (transitions
 *      the project status; the same action also leaves a comment
 *      so the audit trail is intact).
 *
 * Everything that requires more screen real estate (full
 * requirements editing, document review, voting flows) links out
 * to the desktop project page via "Open desktop view". This view
 * deliberately does not try to be the desktop page on a phone.
 */

type StatusCode =
  | "draft"
  | "submitted"
  | "under_management_review"
  | "pending_owner_revisions"
  | "under_board_review"
  | "approved"
  | "conditionally_approved"
  | "rejected"
  | "scheduled"
  | "in_progress"
  | "inspection_required"
  | "completed"
  | "closed"
  | "resubmission_required"
  | "coverage_lapsed";

const STATUS_LABEL: Record<string, string> = {
  in_progress: "In progress",
  inspection_required: "Inspection due",
  scheduled: "Scheduled",
  conditionally_approved: "Conditionally approved",
  completed: "Completed",
  closed: "Closed",
};

const STATUS_TONE: Record<string, string> = {
  in_progress: "bg-status-approved-bg text-status-approved-fg",
  inspection_required: "bg-status-rejected-bg text-status-rejected-fg",
  scheduled: "bg-status-submitted-bg text-status-submitted-fg",
  conditionally_approved: "bg-status-review-bg text-status-review-fg",
  completed: "bg-status-approved-bg text-status-approved-fg",
};

export function InspectProject() {
  const { id = "" } = useParams<{ id: string }>();
  const { state } = useAuth();
  const nav = useNavigate();
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [comments, setComments] = useState<CommentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const [d, c] = await Promise.all([
        api.get<ProjectDetail>(`/api/projects/${id}`),
        api.get<{ data: CommentRow[] }>(`/api/projects/${id}/comments`),
      ]);
      setDetail(d);
      setComments(c.data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? `Load failed (${err.status})` : String(err),
      );
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (state.status !== "authenticated") return null;
  const allowed = hasAnyRole(state.me, [
    "property_manager",
    "admin",
    "chief_engineer",
  ]);
  if (!allowed) {
    return (
      <div className="px-4 py-8">
        <p className="text-ink-600 text-[14px] m-0">
          The inspector view is for PM, Site Admin, and Chief Engineer roles
          only.
        </p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="px-4 py-6">
        {error ? (
          <div className="alert-error">{error}</div>
        ) : (
          <p className="text-ink-500 m-0">Loading…</p>
        )}
      </div>
    );
  }

  const project = detail.data;
  const status = (detail.statusCode ?? project.statusId) as StatusCode;
  const isCEHere = state.me.roles.some(
    (r) =>
      r.buildingId === project.buildingId && r.code === "chief_engineer",
  );
  const isPmHere = state.me.roles.some(
    (r) =>
      r.buildingId === project.buildingId &&
      (r.code === "property_manager" || r.code === "admin"),
  );

  const canSignOffInspection =
    (isCEHere || isPmHere) && status === "inspection_required";
  const canMarkComplete = isPmHere && status === "in_progress";

  const openItems = detail.requirements.filter(
    (r) => r.isRequired && !r.fulfilled,
  );

  return (
    <div className="-mt-6 md:mt-0 -mx-4 sm:-mx-6 lg:-mx-8 md:mx-0">
      <div className="px-4 pt-3 pb-3 bg-brand-800 text-white sticky top-[58px] z-10">
        <button
          type="button"
          onClick={() => nav("/inspect")}
          className="inline-flex items-center gap-1 text-[12px] text-white/70 hover:text-white mb-1.5"
        >
          <ArrowLeft size={13} />
          Inspector queue
        </button>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-[17px] font-display tracking-[-0.01em] m-0 truncate">
              {project.title}
            </h1>
            <div className="text-[12px] text-white/60 mt-0.5 inline-flex items-center gap-1.5">
              <Building2 size={11} />
              Unit {detail.owners[0]?.name ?? ""} · category{" "}
              {detail.categoryCode ?? "—"}
            </div>
          </div>
          <span
            className={cx(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold whitespace-nowrap",
              STATUS_TONE[status] ?? "bg-white/10 text-white",
            )}
          >
            {STATUS_LABEL[status] ?? status}
          </span>
        </div>
        <Link
          to={`/projects/${project.id}`}
          className="inline-flex items-center gap-1 text-[11.5px] text-white/70 hover:text-white mt-2"
        >
          <ExternalLink size={11} />
          Open desktop view
        </Link>
      </div>

      <div className="px-3 py-3 flex flex-col gap-3">
        {/*
          Three quick actions live at the top so a PM/CE who opened
          the project knowing what they wanted to do is one tap
          from doing it. Camera button opens the device camera
          directly via input[capture="environment"]; the photo
          uploads to the existing documents pipeline.
        */}
        <QuickActions
          projectId={project.id}
          buildingId={project.buildingId}
          canSignOffInspection={canSignOffInspection}
          canMarkComplete={canMarkComplete}
          onActed={reload}
        />

        {openItems.length > 0 && (
          <Section title={`Open requirements (${openItems.length})`} icon={<ClipboardCheck size={14} />}>
            <ul className="m-0 p-0 list-none flex flex-col gap-1">
              {openItems.map((r) => (
                <RequirementRow key={r.requirementId} req={r} />
              ))}
            </ul>
          </Section>
        )}
        {openItems.length === 0 && (
          <Section title="Requirements" icon={<CheckCircle2 size={14} />}>
            <p className="text-[13px] text-status-approved-fg m-0 inline-flex items-center gap-1">
              <Check size={13} />
              All required items are fulfilled.
            </p>
          </Section>
        )}

        <RecentComments comments={comments} />
      </div>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-md border border-sand-200 p-3.5">
      <h2 className="text-ink-800 text-[13.5px] m-0 mb-2 inline-flex items-center gap-1.5">
        {icon}
        {title}
      </h2>
      {children}
    </div>
  );
}

function RequirementRow({ req }: { req: Requirement }) {
  return (
    <li className="flex items-start gap-2 py-1 text-[13px] text-ink-700">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-status-rejected-fg mt-1.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="truncate">{req.documentTypeName}</div>
        <div className="text-[11px] text-ink-500">
          required from {req.requiredFrom.replace("_", " ")}
        </div>
      </div>
    </li>
  );
}

function RecentComments({ comments }: { comments: CommentRow[] | null }) {
  const [showAll, setShowAll] = useState(false);
  if (comments === null) return null;
  if (comments.length === 0) {
    return (
      <Section title="Comments" icon={<MessageCircle size={14} />}>
        <p className="text-[12px] text-ink-500 m-0">No comments yet.</p>
      </Section>
    );
  }
  const visible = showAll ? comments : comments.slice(-3);
  return (
    <Section
      title={`Comments · ${comments.length}`}
      icon={<MessageCircle size={14} />}
    >
      <ul className="m-0 p-0 list-none flex flex-col gap-2">
        {visible.map((c) => (
          <li
            key={c.id}
            className={cx(
              "rounded-md border p-2.5",
              c.visibility === "internal"
                ? "bg-ink-50 border-ink-200 border-dashed"
                : "bg-sand-50 border-sand-200",
            )}
          >
            <div className="text-[10.5px] text-ink-500 mb-0.5">
              {new Date(c.createdAt).toLocaleString()}
              {c.visibility === "internal" && " · internal"}
            </div>
            <div className="text-[12.5px] text-ink-700 whitespace-pre-wrap leading-snug">
              {c.body}
            </div>
          </li>
        ))}
      </ul>
      {comments.length > 3 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-[12px] text-brand-700 hover:text-brand-800"
        >
          {showAll ? "Show fewer" : `Show all ${comments.length}`}
        </button>
      )}
    </Section>
  );
}

function QuickActions({
  projectId,
  buildingId,
  canSignOffInspection,
  canMarkComplete,
  onActed,
}: {
  projectId: string;
  buildingId: string;
  canSignOffInspection: boolean;
  canMarkComplete: boolean;
  onActed: () => void | Promise<void>;
}) {
  void buildingId;
  const [openPanel, setOpenPanel] = useState<
    "photo" | "note" | "signoff" | "complete" | null
  >(null);

  return (
    <div className="bg-white rounded-md border border-sand-200 p-3">
      <h2 className="text-ink-800 text-[13.5px] m-0 mb-2.5">Quick actions</h2>
      <div className="grid grid-cols-2 gap-2">
        <ActionButton
          icon={<Camera size={16} />}
          label="Take photo"
          tone="primary"
          onClick={() => setOpenPanel("photo")}
        />
        <ActionButton
          icon={<MessageCircle size={16} />}
          label="Add note"
          tone="neutral"
          onClick={() => setOpenPanel("note")}
        />
        {canSignOffInspection && (
          <ActionButton
            icon={<Check size={16} />}
            label="Sign off inspection"
            tone="success"
            onClick={() => setOpenPanel("signoff")}
            wide
          />
        )}
        {canMarkComplete && (
          <ActionButton
            icon={<CheckCircle2 size={16} />}
            label="Mark work complete"
            tone="success"
            onClick={() => setOpenPanel("complete")}
            wide
          />
        )}
      </div>

      {openPanel === "photo" && (
        <PhotoCapturePanel
          projectId={projectId}
          onClose={() => setOpenPanel(null)}
          onUploaded={async () => {
            setOpenPanel(null);
            await onActed();
          }}
        />
      )}
      {openPanel === "note" && (
        <InspectorActionPanel
          kind="comment_only"
          title="Quick note"
          description='Drops a comment on the project thread. Prefix with "internal:" to keep it staff-only.'
          projectId={projectId}
          onClose={() => setOpenPanel(null)}
          onDone={async () => {
            setOpenPanel(null);
            await onActed();
          }}
        />
      )}
      {openPanel === "signoff" && (
        <InspectorActionPanel
          kind="inspection_signoff"
          title="Sign off inspection"
          description="Moves the project from Inspection due back to In progress. The signed-off note is recorded as a comment on the thread."
          projectId={projectId}
          onClose={() => setOpenPanel(null)}
          onDone={async () => {
            setOpenPanel(null);
            await onActed();
          }}
        />
      )}
      {openPanel === "complete" && (
        <InspectorActionPanel
          kind="work_complete"
          title="Mark work complete"
          description="Moves the project to Completed. Owner and Board are notified. Closeout checklist still runs through the desktop view."
          projectId={projectId}
          onClose={() => setOpenPanel(null)}
          onDone={async () => {
            setOpenPanel(null);
            await onActed();
          }}
        />
      )}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  tone,
  wide,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  tone: "primary" | "neutral" | "success";
  wide?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "inline-flex items-center justify-center gap-2 px-3 py-3 rounded-md text-[13px] font-medium border active:scale-[0.99]",
        wide && "col-span-2",
        tone === "primary" &&
          "bg-brand-700 text-white border-brand-700 hover:bg-brand-800",
        tone === "neutral" &&
          "bg-white text-ink-700 border-sand-200 hover:bg-sand-50",
        tone === "success" &&
          "bg-status-approved-bg text-status-approved-fg border-status-approved-fg/20",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function PhotoCapturePanel({
  projectId,
  onClose,
  onUploaded,
}: {
  projectId: string;
  onClose: () => void;
  onUploaded: () => void | Promise<void>;
}) {
  // capture="environment" tells the browser to launch the rear
  // camera straight into the file picker — exactly the behavior
  // a PM/CE walking the building wants. Falls back gracefully on
  // desktops to a regular file picker.
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docTypeCode, setDocTypeCode] = useState("before_photos");

  useEffect(() => {
    inputRef.current?.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("documentTypeCode", docTypeCode);
      const res = await fetch(`/api/projects/${projectId}/documents`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      await onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-3 border-t border-sand-100 pt-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[12.5px] text-ink-700 font-medium">Take a photo</div>
        <button
          type="button"
          onClick={onClose}
          className="text-[11.5px] text-ink-500 hover:text-ink-700"
        >
          Cancel
        </button>
      </div>
      <select
        className="select w-full mb-2"
        value={docTypeCode}
        onChange={(e) => setDocTypeCode(e.target.value)}
      >
        <option value="before_photos">Before photos</option>
        <option value="after_photos">After photos</option>
      </select>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onChange}
        className="block w-full text-[12px] text-ink-700"
        disabled={busy}
      />
      {busy && (
        <p className="text-[12px] text-ink-500 mt-2 inline-flex items-center gap-1">
          <Upload size={12} />
          Uploading…
        </p>
      )}
      {error && <p className="text-[12px] text-status-rejected-fg mt-2">{error}</p>}
      <p className="text-[10.5px] text-ink-400 mt-2 leading-snug">
        Photos run through the same virus scan as desktop uploads. The picker
        opens the rear camera on phones; on a laptop it acts as a regular
        file chooser.
      </p>
    </div>
  );
}

function InspectorActionPanel({
  projectId,
  kind,
  title,
  description,
  onClose,
  onDone,
}: {
  projectId: string;
  kind: "comment_only" | "inspection_signoff" | "work_complete";
  title: string;
  description: string;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/api/management/projects/${projectId}/inspector-action`,
        {
          kind,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      );
      await onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; currentStatus?: string };
        if (body?.error === "wrong_status") {
          setError(
            `The project is now in '${body.currentStatus}'. Refresh and try again.`,
          );
        } else if (body?.error === "forbidden") {
          setError("You don't have permission to make that change.");
        } else {
          setError(JSON.stringify(err.body));
        }
      } else {
        setError(String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 border-t border-sand-100 pt-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[12.5px] text-ink-700 font-medium">{title}</div>
        <button
          type="button"
          onClick={onClose}
          className="text-[11.5px] text-ink-500 hover:text-ink-700"
        >
          Cancel
        </button>
      </div>
      <p className="text-[11.5px] text-ink-500 leading-snug mb-2 m-0">
        {description}
      </p>
      <textarea
        className="textarea w-full"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={
          kind === "comment_only"
            ? "What did you want to flag?"
            : "Optional notes (visible on the project thread)"
        }
        rows={3}
        autoFocus
        required={kind === "comment_only"}
      />
      {error && <p className="text-[12px] text-status-rejected-fg mt-2">{error}</p>}
      <button
        type="submit"
        disabled={busy || (kind === "comment_only" && !notes.trim())}
        className={cx(
          "mt-2 inline-flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-md text-[13px] font-medium",
          "bg-brand-700 text-white hover:bg-brand-800 active:scale-[0.99]",
          (busy || (kind === "comment_only" && !notes.trim())) && "opacity-60",
        )}
      >
        {kind === "comment_only" ? (
          <>
            <Send size={14} />
            {busy ? "Posting…" : "Post note"}
          </>
        ) : (
          <>
            <Check size={14} />
            {busy ? "Saving…" : "Confirm"}
          </>
        )}
      </button>
      {kind !== "comment_only" && (
        <p className="text-[10.5px] text-ink-400 mt-2 m-0 leading-snug inline-flex items-start gap-1">
          <Clock size={11} className="mt-0.5 flex-shrink-0" />
          The transition is recorded in the audit log. Owner and any
          subscribed reviewers receive an in-app notification immediately and
          an email if their preferences allow it.
        </p>
      )}
    </form>
  );
}
